/**
 * Wallet screen (spec §7 screen 6).
 *
 * "balance, deposit, withdraw, transactions, spend-this-week visible."
 *
 * The spend-this-week figure is not tucked into a settings page. Spec §8 wants
 * it in the header, where a user sees it without looking for it — the whole
 * point of the control is that it is visible to someone who is not currently
 * thinking about limits.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Wallet as WalletData, type WalletTransaction } from './api.js';
import { formatTzs, formatTzsExact, parseSenti } from './money.js';
import { S } from './ui.js';

const QUICK_AMOUNTS = ['100000', '500000', '1000000', '5000000']; // 1k / 5k / 10k / 50k TZS

export function Wallet(): React.JSX.Element {
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<readonly WalletTransaction[]>([]);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [w, t] = await Promise.all([api.wallet(), api.transactions()]);
      setWallet(w);
      setTransactions(t.transactions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Imeshindikana kupakia.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (kind: 'deposit' | 'withdraw'): Promise<void> => {
    const senti = parseSenti(amount.replace(/\D/g, '') + '00'); // typed in whole TZS
    if (senti <= 0n || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (kind === 'deposit') {
        const r = await api.deposit(senti.toString());
        setNotice(r.message);
      } else {
        const r = await api.withdraw(senti.toString());
        setNotice(
          r.status === 'awaiting_approval'
            ? 'Ombi lako linakaguliwa na timu yetu.'
            : 'Ombi limetumwa. Utapokea pesa muda mfupi.',
        );
      }
      setAmount('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Imeshindikana.');
    } finally {
      setBusy(false);
    }
  };

  if (wallet === null) {
    return (
      <main style={S.page}>
        <p style={S.muted}>{error ?? 'Inapakia…'}</p>
      </main>
    );
  }

  const spent = parseSenti(wallet.spentThisWeekSenti);
  const cap = parseSenti(wallet.weeklyCapSenti);
  const capUsedPercent = cap > 0n ? Number((spent * 100n) / cap) : 0;

  return (
    <main style={S.page}>
      <section style={{ ...S.card, marginBottom: 16 }}>
        <div style={S.muted}>Salio lako</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--accent)' }}>
          {formatTzsExact(wallet.balanceSenti)} TZS
        </div>

        {/* Spec §8: spend-this-week, in the header, always visible. */}
        <div style={{ marginTop: 14 }}>
          <div style={{ ...S.row, ...S.muted }}>
            <span>Umetumia wiki hii</span>
            <span>
              {formatTzs(wallet.spentThisWeekSenti)} / {formatTzs(wallet.weeklyCapSenti)} TZS
            </span>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: 'var(--border)',
              marginTop: 6,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, capUsedPercent)}%`,
                height: '100%',
                background: capUsedPercent >= 90 ? '#fca5a5' : 'var(--win)',
              }}
            />
          </div>
        </div>

        {wallet.selfExcludedUntil !== null && (
          <p style={{ ...S.error, marginBottom: 0, marginTop: 12 }}>
            Umejizuia kucheza hadi {new Date(wallet.selfExcludedUntil).toLocaleDateString()}.
          </p>
        )}
      </section>

      <section style={{ ...S.card, marginBottom: 16 }}>
        <h2 style={S.h2}>Weka au toa pesa</h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 6,
            marginBottom: 10,
          }}
        >
          {QUICK_AMOUNTS.map((a) => (
            <button
              key={a}
              type="button"
              style={{ ...S.button, padding: '10px 4px', fontSize: 13 }}
              onClick={() => setAmount((parseSenti(a) / 100n).toString())}
            >
              {formatTzs(a)}
            </button>
          ))}
        </div>

        <input
          style={{ ...S.input, marginBottom: 10 }}
          inputMode="numeric"
          placeholder="Kiasi (TZS)"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            type="button"
            style={amount.length > 0 && !busy ? S.buttonPrimary : S.buttonDisabled}
            disabled={amount.length === 0 || busy}
            onClick={() => void act('deposit')}
          >
            Weka pesa
          </button>
          <button
            type="button"
            style={{ ...S.button, minHeight: 52 }}
            disabled={amount.length === 0 || busy}
            onClick={() => void act('withdraw')}
          >
            Toa pesa
          </button>
        </div>

        {notice !== null && <p style={{ ...S.success, marginBottom: 0 }}>{notice}</p>}
        {error !== null && <p style={{ ...S.error, marginBottom: 0 }}>{error}</p>}
      </section>

      <section style={S.card}>
        <h2 style={S.h2}>Miamala</h2>
        {transactions.length === 0 && <p style={S.muted}>Bado hakuna miamala.</p>}
        {transactions.map((t, i) => {
          const amountSenti = parseSenti(t.amountSenti);
          const positive = amountSenti > 0n;
          return (
            <div
              key={`${t.occurredAt}-${i}`}
              style={{
                ...S.row,
                padding: '10px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              }}
            >
              <div>
                <div style={{ fontSize: 14 }}>{describeKind(t.kind)}</div>
                <div style={{ ...S.muted, fontSize: 12 }}>
                  {new Date(t.occurredAt).toLocaleString()}
                </div>
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: positive ? 'var(--win)' : 'var(--text)',
                }}
              >
                {positive ? '+' : ''}
                {formatTzsExact(amountSenti)}
              </div>
            </div>
          );
        })}
      </section>
    </main>
  );
}

function describeKind(kind: string): string {
  switch (kind) {
    case 'deposit':
      return 'Umeweka pesa';
    case 'withdrawal':
      return 'Umetoa pesa';
    case 'stake':
      return 'Umenunua tiketi';
    case 'payout':
      return 'Ushindi';
    case 'refund':
      return 'Marejesho';
    case 'manual_adjustment':
      return 'Marekebisho';
    default:
      return kind;
  }
}
